import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Calendar } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { useAtomValue } from 'jotai';
import {
  Backdrop,
  CircularProgress,
  GlobalStyles,
  Paper,
  useTheme,
} from '@mui/material';
import { themeAtom } from '../../../../Atoms';
import {
  LOCALIZER,
  FORMATS,
  MAX_TIME,
  MIN_TIME,
  getEventStyle,
} from '../../Utils/constants';
import { culture } from '../../../../Helpers/dates';
import CustomizedToolbar from './CustomizedToolbar';
import EventCard from './EventCard';

const DragAndDropCalendar = withDragAndDrop(Calendar);

const CalendarPaper = function CalendarPaper({
  events,
  loading = false,
  handleChangeRange,
  onSelectEvent,
  onSelectSlot,
  onEventDrop,
  handleOpenAddDialog,
}) {
  const intl = useIntl();
  const theme = useTheme();
  const currentTheme = useAtomValue(themeAtom);

  const messages = useMemo(
    () => ({
      month: intl.messages.MONTH,
      week: intl.messages.WEEK,
      work_week: intl.messages.WEEK,
      day: intl.messages.DAY,
      today: intl.messages.TODAY,
      showMore: (total) => intl.formatMessage({ id: 'SHOW_MORE' }, { total }),
    }),
    [intl]
  );

  const components = useMemo(
    () => ({
      event: EventCard,
      toolbar: (props) => (
        <CustomizedToolbar {...props} handleOpenAddDialog={handleOpenAddDialog} />
      ),
    }),
    [handleOpenAddDialog]
  );

  const eventPropGetter = useMemo(
    () => (event) =>
      getEventStyle(event.type, event?.outcome === 17, theme),
    [theme]
  );

  const overlayStyles = useMemo(
    () => ({
      // The "+N more" popup portals to <body>, outside this Paper, so it can't
      // inherit the calendar's CSS variables — theme it directly here.
      '.rbc-overlay': {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.divider}`,
      },
      '.rbc-overlay-header': {
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    }),
    [theme]
  );

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'relative',
        width: 'auto',
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        p: 2,
        boxSizing: 'border-box',
        '--rbc-primary': theme.palette.primary.main,
        '--rbc-header-color': theme.palette.text.secondary,
        '--rbc-today-bg': theme.palette.action.hover,
        '--rbc-hover-bg': theme.palette.action.hover,
        '--rbc-grid-line': theme.palette.divider,
        '--rbc-border': theme.palette.divider,
        '--rbc-offrange-bg':
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
        '--rbc-offrange-color': theme.palette.text.disabled,
      }}
    >
      <GlobalStyles styles={overlayStyles} />
      <DragAndDropCalendar
        className={
          currentTheme === 'dark' ? 'rbc-calendar-dark' : 'rbc-calendar-light'
        }
        onRangeChange={handleChangeRange}
        defaultView="work_week"
        views={['day', 'work_week', 'month']}
        culture={culture}
        popup
        selectable
        resizable={false}
        resizableAccessor={() => false}
        dayLayoutAlgorithm="no-overlap"
        step={30}
        timeslots={2}
        events={events}
        localizer={LOCALIZER}
        formats={FORMATS}
        startAccessor="startDate"
        endAccessor="endDate"
        style={{ height: '100%' }}
        messages={messages}
        eventPropGetter={eventPropGetter}
        components={components}
        onSelectEvent={onSelectEvent}
        onSelectSlot={onSelectSlot}
        onEventDrop={onEventDrop}
        draggableAccessor={() => true}
        min={MIN_TIME}
        max={MAX_TIME}
      />
      <Backdrop
        open={loading}
        sx={{
          position: 'absolute',
          zIndex: (t) => t.zIndex.drawer - 1,
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          color: 'primary.main',
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
};

export default CalendarPaper;
